import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { adminContracts } from './generated/adminContracts'
import { adminScreens } from './generated/adminScreens'
import { specializedScreens } from './generated/specializedScreens'
import SignedOutPage from './pages/SignedOutPage.vue'
import LoginPage from './pages/LoginPage.vue'
import { adminSessionApi, applyAdminSession } from './api/adminSession'
import { AdminApiError } from './api/adminClient'
import { useSessionStore } from './stores/session'
import { adminQueryClient } from './queryClient'

function vuePath(path: string): string {
  return path.replaceAll(/\{([^}]+)\}/g, ':$1')
}

export const routes: RouteRecordRaw[] = adminContracts.map((contract) => ({
  path: vuePath(contract.path),
  name: contract.id,
  component: specializedScreens[contract.id] ?? adminScreens[contract.id],
  meta: {
    contractId: contract.id,
    requiresAuth: true,
    title: contract.title,
    actions: contract.actions,
    testIds: contract.testIds,
  },
}))

routes.push({ path: '/login', name: 'admin-login', component: LoginPage, meta: { safeEntry: true, title: '管理员登录' } })
routes.push({ path: '/signed-out', name: 'admin-signed-out', component: SignedOutPage, meta: { safeEntry: true, title: '已安全退出' } })
routes.push({ path: '/', redirect: '/overview' })
routes.push({ path: '/:pathMatch(.*)*', redirect: '/overview' })

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

type AuthState = 'unknown' | 'authenticated' | 'anonymous'
let authState: AuthState = 'unknown'
let probe: Promise<boolean> | null = null

export function safeReturnPath(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return '/overview'
  return value
}

export function resetAdminAuthState() {
  authState = 'unknown'
  probe = null
}

export function markAdminAuthenticated() {
  authState = 'authenticated'
  probe = null
}

export function markAdminAnonymous() {
  authState = 'anonymous'
  probe = null
}

function clearClientAuthority() {
  useSessionStore().clearSensitiveState()
  adminQueryClient.clear()
}

async function sessionProbe(): Promise<boolean> {
  if (authState === 'authenticated') return true
  if (authState === 'anonymous') return false
  if (!probe) {
    probe = adminSessionApi.getSession()
      .then((session) => {
        applyAdminSession(session)
        authState = 'authenticated'
        return true
      })
      .catch((error: unknown) => {
        if (error instanceof AdminApiError && [401, 410].includes(error.status)) {
          authState = 'anonymous'
          clearClientAuthority()
          return false
        }
        throw error
      })
      .finally(() => {
        probe = null
      })
  }
  return probe
}

router.beforeEach(async (to) => {
  if (to.name === 'admin-login') {
    try {
      if (await sessionProbe()) return { path: '/overview' }
    } catch {
      // Keep the login form available while the API is unreachable.
    }
    return true
  }
  if (to.name === 'admin-signed-out') return true
  if (to.meta.requiresAuth !== true) return true
  try {
    if (await sessionProbe()) return true
  } catch {
    clearClientAuthority()
  }
  return { name: 'admin-login', query: { return_to: safeReturnPath(to.fullPath) } }
})
